/**
 * TargetingCursor - Glowing reticle that follows the mouse when targeting is active.
 * Shown when a unit has a deploy effect (or other order) that requires picking a target,
 * when attack mode is active for an eligible attacker, or when a deployable unit is selected from hand.
 * Uses pointer-events: none so it does not block clicks.
 *
 * @param {'target'|'attack'|'deploy'} [variant='target'] - Visual style; default matches legacy yellow targeting.
 */

import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TARGETING_CURSOR_SUPPRESS_CLASS, BATTLE_TARGETING_Z_INDEX } from '../config/cursorConfig';
import './TargetingCursor.css';

const VARIANT_CLASS = {
  target: 'targeting-cursor--target',
  attack: 'targeting-cursor--attack',
  deploy: 'targeting-cursor--deploy',
};

/** Appended at end of document.head so it wins the cascade. */
const SUPPRESS_STYLE_ELEMENT_ID = 'targeting-cursor-suppress-system-pointer-css';

function clearRootInlineCursor() {
  if (typeof document === 'undefined') return;
  try {
    document.documentElement.style.removeProperty('cursor');
    document.body.style.removeProperty('cursor');
  } catch (_) {
    /* ignore */
  }
}

function applySuppressSystemCursor(active) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (!active) {
    root.classList.remove(TARGETING_CURSOR_SUPPRESS_CLASS);
    const existing = document.getElementById(SUPPRESS_STYLE_ELEMENT_ID);
    if (existing) existing.remove();
    return;
  }

  let styleEl = document.getElementById(SUPPRESS_STYLE_ELEMENT_ID);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = SUPPRESS_STYLE_ELEMENT_ID;
    styleEl.setAttribute('data-from', 'TargetingCursor');
    document.head.appendChild(styleEl);
  }

  const c = TARGETING_CURSOR_SUPPRESS_CLASS;
  styleEl.textContent = `
html.${c},
html.${c} body,
html.${c} *,
html.${c} *::before,
html.${c} *::after {
  cursor: none !important;
}
`;

  root.classList.add(TARGETING_CURSOR_SUPPRESS_CLASS);
}

export function TargetingCursor({ active, variant = 'target' }) {
  const reticleRef = useRef(null);
  const variantClass = VARIANT_CLASS[variant] || VARIANT_CLASS.target;
  /** Last element that received inline cursor:none — cleared so cascade can restore after targeting */
  const lastHitForCursorRef = useRef(null);

  useLayoutEffect(() => {
    applySuppressSystemCursor(Boolean(active));
    return () => {
      applySuppressSystemCursor(false);
    };
  }, [active]);

  useLayoutEffect(() => {
    if (!active) {
      clearRootInlineCursor();
      if (lastHitForCursorRef.current) {
        try {
          lastHitForCursorRef.current.style.removeProperty('cursor');
        } catch (_) {
          /* node may be detached */
        }
        lastHitForCursorRef.current = null;
      }
      return undefined;
    }

    const applyReticlePosition = (x, y) => {
      const reticle = reticleRef.current;
      if (!reticle) return;
      reticle.style.left = `${x}px`;
      reticle.style.top = `${y}px`;
    };

    const clearLastHitCursor = () => {
      if (lastHitForCursorRef.current) {
        try {
          lastHitForCursorRef.current.style.removeProperty('cursor');
        } catch (_) {
          /* detached */
        }
        lastHitForCursorRef.current = null;
      }
    };

    const applyCursorNoneOnHitTarget = (e) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      const top =
        path.find((n) => n && n.nodeType === 1 && typeof n.style?.setProperty === 'function') ||
        (e.target && e.target.nodeType === 1 && typeof e.target.style?.setProperty === 'function'
          ? e.target
          : e.target?.parentElement);

      if (lastHitForCursorRef.current && lastHitForCursorRef.current !== top) {
        try {
          lastHitForCursorRef.current.style.removeProperty('cursor');
        } catch (_) {
          /* detached */
        }
      }

      lastHitForCursorRef.current = top || null;
      if (lastHitForCursorRef.current) {
        lastHitForCursorRef.current.style.setProperty('cursor', 'none', 'important');
      }
    };

    /** Root + hit target: Windows often still paints the OS arrow unless inline none beats all CSS. */
    const syncCursorSuppressFromEvent = (e) => {
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('cursor', 'none', 'important');
        document.body.style.setProperty('cursor', 'none', 'important');
      }
      applyCursorNoneOnHitTarget(e);
    };

    const onPointerMove = (e) => {
      applyReticlePosition(e.clientX, e.clientY);
      syncCursorSuppressFromEvent(e);
    };

    const cx = typeof window !== 'undefined' ? window.innerWidth / 2 : 0;
    const cy = typeof window !== 'undefined' ? window.innerHeight / 2 : 0;
    applyReticlePosition(cx, cy);

    const primeHitTarget = () => {
      if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return;
      document.documentElement.style.setProperty('cursor', 'none', 'important');
      document.body.style.setProperty('cursor', 'none', 'important');
      const px = Math.max(0, Math.floor(window.innerWidth / 2));
      const py = Math.max(0, Math.floor(window.innerHeight / 2));
      const under = document.elementFromPoint(px, py);
      if (under && typeof under.style?.setProperty === 'function') {
        clearLastHitCursor();
        lastHitForCursorRef.current = under;
        under.style.setProperty('cursor', 'none', 'important');
      }
    };

    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerdown', syncCursorSuppressFromEvent, true);
    const raf = window.requestAnimationFrame(primeHitTarget);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onPointerMove, true);
      window.removeEventListener('pointerdown', syncCursorSuppressFromEvent, true);
      clearRootInlineCursor();
      clearLastHitCursor();
    };
  }, [active]);

  if (!active) return null;

  if (typeof document === 'undefined') {
    return null;
  }

  const isAttackCross = variant === 'attack';

  return createPortal(
    <div
      className="targeting-cursor-overlay"
      style={{ zIndex: BATTLE_TARGETING_Z_INDEX.targetingCursor }}
      aria-hidden="true"
    >
      <div
        ref={reticleRef}
        className={`targeting-cursor-reticle ${variantClass}`}
      >
        {isAttackCross ? (
          <div className="targeting-cursor-attack-cross">
            <span className="targeting-cursor-attack-arm targeting-cursor-attack-arm--horizontal" />
            <span className="targeting-cursor-attack-arm targeting-cursor-attack-arm--vertical" />
          </div>
        ) : (
          <>
            <div className="targeting-cursor-ring" />
            <div className="targeting-cursor-dot" />
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default TargetingCursor;
